import React, { useState } from 'react';
import { Edit2, Send, CheckCircle, AlertTriangle, Hash } from 'lucide-react';
import AIBroadcastGenerator from './AIBroadcastGenerator'; // 🚨 NEW: Import the AI Assistant

// The Master List of Standardized Nommia Channels
const STANDARD_CHANNELS = [
  { id: 'general-chat', name: 'General Lounge' },
  { id: 'vip-signals', name: 'VIP Trade Signals' },
  { id: 'crypto-analysis', name: 'Crypto Analysis' },
  { id: 'forex-beginners', name: 'Forex Beginners' },
  { id: 'risk-management-pro', name: 'Risk Management Pro' }
];

const GhostwriterTab = ({ communities, availablePartners }) => {
  const [selectedHub, setSelectedHub] = useState('');
  const [targetChannel, setTargetChannel] = useState(''); // Now starts empty to force a selection
  const [messageContent, setMessageContent] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [status, setStatus] = useState({ type: '', text: '' });

  const handleSendBroadcast = async () => {
    if (!selectedHub || !messageContent || !targetChannel) {
      setStatus({ type: 'error', text: 'Please select an audience, a channel, and write a message.' });
      return;
    }

    setIsSending(true);
    setStatus({ type: '', text: '' });

    try {
      // 1. Determine which Hubs we are targeting
      let targetHubs = [];
      if (selectedHub === 'all') {
        targetHubs = communities;
      } else {
        const singleHub = communities.find(c => c.id === selectedHub);
        if (singleHub) targetHubs = [singleHub];
      }

      // 2. Build the bulk payload with Dynamic Impersonation
      const blasts = targetHubs.map(hub => {
        const leadId = hub.community_leads?.[0]?.lead_user_id;
        const partner = availablePartners?.find(p => p.id === leadId);

        return {
          communityId: hub.id,
          leadEmail: partner ? partner.email : "admin@nommia.io",
          leadName: partner ? partner.name : "Nommia System",
          content: messageContent,
          channelName: targetChannel // Pulls the exact ID from the new dropdown
        };
      });

      // 3. Fire the bulk payload to the backend
      const backendUrl = 'https://nommia-crm-backend-production.up.railway.app'; 
      
      const response = await fetch(`${backendUrl}/api/ghostwriter/broadcast`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ blasts }),
      });

      const data = await response.json();

      if (response.ok) {
        setStatus({ type: 'success', text: `Boom! Message dropped into #${targetChannel} for ${blasts.length} Hub(s).` });
        setMessageContent('');
        setTargetChannel(''); // Reset channel after sending
      } else {
        setStatus({ type: 'error', text: `Failed: ${data.error}` });
      }
    } catch (error) {
      console.error("Broadcast Error:", error);
      setStatus({ type: 'error', text: 'Network error. Is the backend running?' });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 md:p-8 shadow-xl animate-fade-in mt-8">
      
      <div className="border-b border-slate-800 pb-4 mb-6">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Edit2 className="text-amber-500" /> Ghostwriter Broadcast Engine
        </h2>
        <p className="text-sm text-slate-400 mt-1">Dynamically impersonate Community Leads and drop announcements.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Target Hub Dropdown */}
        <div>
          <label className="block text-xs font-bold text-slate-400 uppercase mb-2 text-purple-400">Target Audience</label>
          <select 
            className="w-full bg-slate-950 border border-purple-500/30 rounded-lg p-3 text-sm text-white focus:outline-none focus:border-purple-500 transition-colors cursor-pointer"
            value={selectedHub}
            onChange={(e) => setSelectedHub(e.target.value)}
          >
            <option value="">-- Select Audience --</option>
            <option value="all" className="font-bold text-amber-500">🌐 BLAST ALL COMMUNITIES</option>
            <optgroup label="Specific Hubs">
              {communities && communities.map((hub) => (
                <option key={hub.id} value={hub.id}>
                  🔒 {hub.name}
                </option>
              ))}
            </optgroup>
          </select>
        </div>

        {/* Target Channel Dropdown (UPGRADED) */}
        <div>
          <label className="block text-xs font-bold text-slate-400 uppercase mb-2 flex items-center gap-1">
            <Hash size={12}/> Target Channel
          </label>
          <select 
            className="w-full bg-slate-950 border border-slate-700 rounded-lg p-3 text-sm text-white focus:outline-none focus:border-amber-500 transition-colors cursor-pointer"
            value={targetChannel}
            onChange={(e) => setTargetChannel(e.target.value)}
            disabled={!selectedHub} // Locks the channel dropdown until an audience is picked
          >
            <option value="">-- Select Room --</option>
            {STANDARD_CHANNELS.map((channel) => (
              <option key={channel.id} value={channel.id}>
                # {channel.name}
              </option>
            ))}
          </select>
          {!selectedHub && <p className="text-[10px] text-slate-500 mt-1">Select an audience first.</p>}
        </div>
      </div>
      
      {/* 🚨 NEW: AI ASSISTANT DROP-IN */}
      <AIBroadcastGenerator 
          onUseMessage={(msg) => setMessageContent(msg)} 
      />

      {/* Message Text Area */}
      <div className="mb-6">
        <label className="block text-xs font-bold text-slate-400 uppercase mb-2">Message Content</label>
        <textarea 
          className="w-full bg-slate-950 border border-slate-700 rounded-lg p-4 text-sm text-white h-32 resize-none focus:outline-none focus:border-amber-500 transition-colors"
          placeholder="Write the announcement..."
          value={messageContent}
          onChange={(e) => setMessageContent(e.target.value)}
        />
      </div>

      {/* Status Message */}
      {status.text && (
        <div className={`mb-6 p-4 rounded-lg border text-sm flex items-center gap-2 font-medium ${
          status.type === 'error' 
            ? 'bg-red-500/10 border-red-500/20 text-red-400' 
            : 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400'
        }`}>
          {status.type === 'error' ? <AlertTriangle size={18} /> : <CheckCircle size={18} />}
          {status.text}
        </div>
      )}

      {/* Send Button */}
      <div className="flex justify-end pt-2">
        <button 
          onClick={handleSendBroadcast}
          disabled={isSending}
          className={`px-8 py-2.5 rounded-lg text-sm font-bold text-white transition-colors flex items-center gap-2 shadow-lg ${
            isSending 
              ? 'bg-slate-800 text-slate-500 cursor-not-allowed border border-slate-700' 
              : 'bg-amber-600 hover:bg-amber-500 shadow-amber-900/20'
          }`}
        >
          <Send size={16} />
          {isSending ? 'Transmitting...' : 'Initiate Broadcast'}
        </button>
      </div>

    </div>
  );
};

export default GhostwriterTab;